import { Link, matchPath, Navigate, useLocation, useNavigate } from 'react-router';
import { useMe } from '../api/useMe.ts';
import { useTilgang } from '../skall/tilgang.ts';
import { Flate, Varsel } from './deler.tsx';
import { HInnboks } from './HInnboks.tsx';
import { HInnstillinger, UNDERSIDER, type Underside } from './HInnstillinger.tsx';
import { HRegister } from './HRegister.tsx';
import { HTilDeg } from './HTilDeg.tsx';
import { erHBehandler, erHLeserPluss, H_INNBOKS, H_MINE, H_TIL_DEG, HYPOTESER } from './rommet.ts';
import { Innsending } from './Innsending.tsx';
import { JusterSide } from './Juster.tsx';
import { EndreSide } from './Endre.tsx';
import { HypotesenMin } from './HypotesenMin.tsx';
import { HypoteseSide } from './Kvittering.tsx';
import { MineSide } from './MineSide.tsx';
import { LeggInnSide } from './LeggInn.tsx';
import { NavnSide } from './NavnSide.tsx';
import { KonklusjonSide } from './Konklusjon.tsx';
import { Kildekatalog } from './Kildekatalog.tsx';
import { KildeSporsmaalSide } from './KildeSporsmaal.tsx';
import { OppstartSide } from './Oppstart.tsx';
import { MoeterSide } from './Moeter.tsx';

export { HYPOTESER };

/**
 * Innsendermodus (H2.2; `ux-design.md` 2.1): rutene under /h, i panel-appen, med panelets ene
 * breakpoint. ÉN komponent leser adressen selv, så skjemaet på /h/ny og /h/utkast/{id}/{ledd}
 * er samme instans — teksten i minnet overlever at adressen får utkastets id etter første
 * lagring, og at nettleserens tilbake-knapp går ett ledd tilbake.
 *
 * Første runde: /h/mine, /h/ny, /h/utkast/{id}/{ledd}, /h/{nr} (kvitteringen) og /h/navn.
 * /h/kilder er H3.
 *
 * H2.2, resten (PR 1): /h/{nr} er «Hypotesen min» (W8) — kvitteringen bare rett etter «Send inn»
 * (`{ sendt: true }` i historikkens tilstand). PR 2: /h/{nr}/endre er «Endre hypotesen» (W8b, M4d);
 * etter «Send endringen» kommer den tilbake til /h/{nr} med kvitteringen (`{ endret: tekst }`).
 *
 * H2.3, første runde: rommets forside på /h (Innboks for behandler og eier, Mine hypoteser for
 * bidragsyter og leser), /h/innboks/{nr} og /h/til-deg/{nr}. Lista og detaljen er ÉN instans per
 * side (samme `key` med og uten nummer), så filtre, søk og angre-vinduet overlever radbytte.
 *
 * H2.3, andre runde: /h/registeret/{nr} for alle i rommet, og /h/innstillinger med undersidene
 * (statusnavn, avslagskoder, stoppkoder, svarmaler, beslutningstakere) for eier.
 *
 * H2.3 andre runde: /h/innboks/{nr}/juster og /h/til-deg/{nr}/juster er «Juster før start»
 * (konseptets skjerm 6b) — en egen side under detaljen, tilbake til samme liste.
 *
 * H2.2 andre runde, del 3: /h/{nr}/start er oppstartskortet (W10) for eieren; etter «Start
 * testen» går den til /h/til-deg/{nr} med kvitteringen.
 */
export function HModus() {
  const lokasjon = useLocation();
  const sti = lokasjon.pathname.replace(/\/+$/, '') || '/';
  // Rett etter «Send inn» (Innsending.tsx): kvitteringen. Ellers er /h/{nr} hypotesen.
  const sendt = Boolean((lokasjon.state as { sendt?: boolean } | null)?.sendt);
  const tilgang = useTilgang();
  const naviger = useNavigate();
  const me = useMe();

  // Tjeneren vokter hver rute (rom-vakten, H1.1); dette er speilet som forklarer før et kall
  // får 404 (ux-flow-architect, «Forklarte låser»). Ukjent er strengest — skallet venter på «meg».
  if (!tilgang.kanSe(HYPOTESER)) {
    return (
      <Flate tittel="Hypoteser" tilbake={{ navn: 'Panelet', til: '/' }}>
        <Varsel tone="info" rolle="status">
          Hypoteser er ikke skrudd på for deg. Spør eieren hos dere om du skal ha tilgang.
        </Varsel>
      </Flate>
    );
  }

  if (sti === '/h') return <Navigate to={erHBehandler(tilgang) ? H_INNBOKS.sti : H_MINE.sti} replace />;

  // «Juster før start» (konseptets skjerm 6b): egen side under detaljens adresse, tilbake til samme liste.
  const juster = matchPath('/h/:side/:nr/juster', sti);
  const justerNr = juster ? nummerFra(juster.params.nr) : null;
  if (juster && justerNr !== null && (juster.params.side === 'innboks' || juster.params.side === 'til-deg')) {
    if (juster.params.side === 'innboks' && !erHBehandler(tilgang)) return <IkkeDin />;
    return <JusterSide key="h-juster" nr={justerNr} base={`/h/${juster.params.side}`} />;
  }

  const innboks = matchPath('/h/innboks/:nr?', sti);
  if (innboks) {
    if (!erHBehandler(tilgang)) return <IkkeDin />;
    return <HInnboks key="h-innboks" nr={nummerFra(innboks.params.nr)} />;
  }
  // H3.2 (W9, W10): møtet beslutningstakeren skal bekrefte, i «Til deg» — lista til venstre, møtet til høyre.
  const tilDegMoete = matchPath('/h/til-deg/moter/:id', sti);
  if (tilDegMoete?.params.id) return <HTilDeg key="h-til-deg" nr={null} moeteId={tilDegMoete.params.id} />;
  // H3.2 (W1, W11): «Møter» med agendaen og beslutningsloggen, for leser og opp. Et møte på direkte
  // adresse for en bidragsyter er hennes å bekrefte, og står under «Til deg».
  const moeter = matchPath('/h/moter/:id?', sti);
  if (moeter) {
    if (!erHLeserPluss(tilgang)) return moeter.params.id ? <Navigate to={`/h/til-deg/moter/${moeter.params.id}`} replace /> : <MoeterIkkeDine />;
    return <MoeterSide key="h-moeter" id={moeter.params.id ?? null} />;
  }
  // H3.3 (W10): kildeeierens ene utkast, gjennom spørsmålet (Kh15).
  const sporsmaal = matchPath('/h/til-deg/sporsmal/:id', sti);
  if (sporsmaal?.params.id) return <KildeSporsmaalSide key={`sp-${sporsmaal.params.id}`} id={sporsmaal.params.id} />;
  const tilDeg = matchPath('/h/til-deg/:nr?', sti);
  if (tilDeg) return <HTilDeg key="h-til-deg" nr={nummerFra(tilDeg.params.nr)} />;
  const register = matchPath('/h/registeret/:nr?', sti);
  if (register) return <HRegister key="h-registeret" nr={nummerFra(register.params.nr)} />;
  // H3.3 (W9): kildekatalogen under Innstillinger. Alle i rommet leser den; eier holder den, og
  // kildens eier retter sin egen — derfor ikke bak eier-vakten under.
  const katalog = matchPath('/h/innstillinger/kilder/:id?', sti);
  if (katalog) return <Kildekatalog key="h-kilder" id={katalog.params.id ?? null} />;
  const innst = matchPath('/h/innstillinger/:hva?', sti);
  if (innst) {
    const hva = innst.params.hva;
    if (hva && !(hva in UNDERSIDER)) return <Navigate to="/h/innstillinger" replace />;
    if (!tilgang.eier) return <BareEierHer />;
    return <HInnstillinger underside={(hva as Underside | undefined) ?? null} />;
  }
  // H2.8 (DD-80): «Legg inn fra tidligere kanal» — eierens alene; tjeneren vokter med hypoteser:eier.
  if (sti === '/h/legg-inn') return tilgang.eier ? <LeggInnSide key="h-legg-inn" /> : <BareEierHer tekst="Hypoteser fra en tidligere kanal legges inn av en eier hos" />;
  if (sti === '/h/mine') return <MineSide />;
  if (sti === '/h/navn') {
    return <NavnSide tittel="Visningsnavn" forrige={me.data?.displayName} onLagret={() => void naviger(-1)} onLukk={() => void naviger(-1)} />;
  }

  const utkast = matchPath('/h/utkast/:id/:ledd?', sti);
  if (sti === '/h/ny' || utkast) {
    // Samme `key` på begge adressene: det er DETTE som holder teksten i minnet over byttet.
    return <Innsending key="innsending" ruteId={utkast?.params.id} ruteLedd={utkast?.params.ledd} />;
  }

  // Oppstartskortet (W10): eierens side før «Start testen», under hypotesens adresse som «Endre».
  const oppstart = matchPath('/h/:nr/start', sti);
  const oppstartNr = oppstart ? nummerFra(oppstart.params.nr) : null;
  if (oppstartNr !== null) return <OppstartSide key={`start-${oppstartNr}`} nr={oppstartNr} />;

  // Konklusjonen (W11): eierens side når testen går, ved siden av oppstarten.
  const konklusjon = matchPath('/h/:nr/konklusjon', sti);
  const konklusjonNr = konklusjon ? nummerFra(konklusjon.params.nr) : null;
  if (konklusjonNr !== null) return <KonklusjonSide key={`konklusjon-${konklusjonNr}`} nr={konklusjonNr} />;

  const endre = matchPath('/h/:nr/endre', sti);
  const endreNr = endre ? nummerFra(endre.params.nr) : null;
  if (endreNr !== null) return <EndreSide key={`endre-${endreNr}`} nr={endreNr} />;

  const tall = nummerFra(matchPath('/h/:nr', sti)?.params.nr);
  if (tall !== null) return sendt ? <HypoteseSide nr={tall} /> : <HypotesenMin key={tall} nr={tall} />;

  return <Navigate to="/h/mine" replace />;
}

/** «142» eller «H-142» i adressen → 142; noe annet er ingen hypotese. */
function nummerFra(del: string | undefined): number | null {
  if (!del) return null;
  const n = Number(del.replace(/^h-/i, ''));
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * En direkte adresse til en side personen ikke har: én setning om hvem siden er for, og veien
 * til det som er hennes — aldri en tom side og aldri 404 (`admin-hypoteser.jsx`, IkkeDin).
 */
function IkkeDin() {
  return (
    <div className="hb-tom">
      <h1 className="panel-h1">Innboks er for dem som vurderer</h1>
      <p className="hb-dempet">Innboksen er for behandlere i rommet Hypoteser og for eier. Det som er tildelt deg, står under «Til deg».</p>
      <div>
        <Link to={H_TIL_DEG.sti} className="panel-knapp-sekundaer">
          {H_TIL_DEG.navn}
        </Link>
      </div>
    </div>
  );
}

/** «Møter» på direkte adresse for en bidragsyter: siden er for leser og opp; møtet hennes står i «Til deg». */
function MoeterIkkeDine() {
  return (
    <div className="hb-tom">
      <h1 className="panel-h1">Møter er for dem som leser i rommet</h1>
      <p className="hb-dempet">Er du beslutningstaker, står møtet du skal bekrefte under «Til deg».</p>
      <div>
        <Link to={H_TIL_DEG.sti} className="panel-knapp-sekundaer">
          {H_TIL_DEG.navn}
        </Link>
      </div>
    </div>
  );
}

/**
 * Rommets Innstillinger på direkte adresse for et medlem (`admin-hypoteser.jsx`: IkkeDin med
 * «Denne siden er for eier»). Ingenting hentes — `GET /settings` er ikke rommets å lese.
 */
function BareEierHer({ tekst = 'Innstillingene for rommet Hypoteser endres av en eier hos' }: { tekst?: string }) {
  const me = useMe();
  return (
    <div className="hb-tom">
      <h1 className="panel-h1">Denne siden er for eier</h1>
      <p className="hb-dempet">
        {tekst} {me.data?.tenantName ?? 'dere'}.
      </p>
      <div>
        <Link to={H_TIL_DEG.sti} className="panel-knapp-sekundaer">
          {H_TIL_DEG.navn}
        </Link>
      </div>
    </div>
  );
}
